import React, { useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { formatApiError } from "@/lib/utils-app";

/**
 * Lightweight token-based fuzzy score (0..1) for de-duping breeder/kennel
 * names client-side. No external dep — we just want "Maple Ridge Cav" to
 * match "Maple Ridge Cavaliers" while rejecting unrelated names.
 *
 *   1.0  → one string is a substring of the other
 *   ≥0.55 → enough token overlap + prefix-hits to suggest as a dupe
 *   <0.55 → ignored
 */
function fuzzyScore(needle, hay) {
  if (!needle || !hay) return 0;
  const n = String(needle).trim().toLowerCase();
  const h = String(hay).trim().toLowerCase();
  if (!n || !h) return 0;
  if (h.includes(n) || n.includes(h)) return 1;
  const aTokens = n.split(/\s+/).filter(Boolean);
  const bTokens = h.split(/\s+/).filter(Boolean);
  const aSet = new Set(aTokens);
  const bSet = new Set(bTokens);
  if (!aSet.size || !bSet.size) return 0;
  let inter = 0;
  aSet.forEach((t) => { if (bSet.has(t)) inter++; });
  const jaccard = inter / (aSet.size + bSet.size - inter);
  let prefixHits = 0;
  aSet.forEach((t) => {
    if (t.length < 3) return;
    bTokens.forEach((bt) => {
      if (bt.length >= 3 && (bt.startsWith(t) || t.startsWith(bt))) prefixHits++;
    });
  });
  const prefixScore = Math.min(1, prefixHits / Math.max(aSet.size, 1));
  return Math.min(1, jaccard + 0.5 * prefixScore);
}

/**
 * Inline "Create new breeder" dialog used by DogForm + LitterForm.
 *
 * Why this exists: registering a dog requires a breeder. If the
 * breeder isn't already in the dropdown (most often because the
 * registering user is *not* the breeder themselves — e.g. a buyer
 * recording the dog they just bought), the user used to be stuck.
 * This dialog lets any authenticated user create a lightweight
 * breeder record on the spot — no Pro plan or admin required.
 *
 * The created row is NOT auto-verified — only Admin verification
 * (via the kennel verification drawer) confers the Verified Kennel
 * badge. This stops a buyer from accidentally claiming a kennel
 * status they don't hold.
 *
 * Soft client-side de-dupe: as the user types into Breeder name or
 * Kennel name, we fuzzy-match against `existingBreeders` and surface
 * "Did you mean…" chips. Tapping one selects the existing row instead
 * of creating a duplicate, which cuts down on admin merge work.
 *
 * Props:
 *   open               — controlled open state
 *   onClose            — called when the dialog should close
 *   onCreated          — called with the freshly-created breeder row
 *                        (parent typically selects it in their dropdown)
 *   onPickedExisting   — called when the user taps an existing-breeder
 *                        suggestion instead of creating one
 *   defaultName        — optional pre-fill (e.g. text the user already
 *                        typed in a search field)
 *   existingBreeders   — full list of known breeders (used for fuzzy
 *                        match). Pass [] to disable suggestions.
 */
export default function AddBreederDialog({
  open,
  onClose,
  onCreated,
  onPickedExisting,
  defaultName = "",
  existingBreeders = [],
}) {
  const [form, setForm] = useState({
    name: defaultName,
    kennel_name: "",
    location: "",
    email: "",
    phone: "",
    contact: "",
    bio: "",
  });
  const [busy, setBusy] = useState(false);

  // Keep `name` pre-filled when defaultName changes (e.g. parent opens
  // the dialog from a different context).
  React.useEffect(() => {
    if (open) setForm((f) => ({ ...f, name: defaultName || f.name }));
  }, [open, defaultName]);

  // Soft client-side de-dupe: combine breeder-name + kennel-name and
  // fuzzy-match against existing breeders. We surface the top 3
  // suggestions above the form so the user can pick instead of creating
  // a duplicate. Threshold tuned so close typos match ("Maple Ridge
  // Cavaliers" vs "Maple Ridge Cav") but unrelated names don't.
  const suggestions = React.useMemo(() => {
    const needle = `${form.name} ${form.kennel_name}`.trim().toLowerCase();
    if (needle.length < 3 || !Array.isArray(existingBreeders) || !existingBreeders.length) {
      return [];
    }
    const scored = existingBreeders
      .map((b) => {
        const hay = `${b.name || ""} ${b.kennel_name || ""}`.trim().toLowerCase();
        if (!hay) return null;
        const score = fuzzyScore(needle, hay);
        return { breeder: b, score };
      })
      .filter((x) => x && x.score >= 0.55)
      .sort((a, b) => b.score - a.score)
      .slice(0, 3);
    return scored;
  }, [form.name, form.kennel_name, existingBreeders]);

  const pickExisting = (b) => {
    toast.success(`Selected existing breeder: ${b.kennel_name || b.name}`);
    onPickedExisting?.(b);
    setForm({ name: "", kennel_name: "", location: "", email: "", phone: "", contact: "", bio: "" });
    onClose?.();
  };

  const submit = async () => {
    if (!form.name.trim()) {
      toast.error("Breeder name is required.");
      return;
    }
    // Backend enforces this too, but failing fast on the client
    // means the user doesn't burn a network round-trip.
    if (!form.email.trim() && !form.phone.trim()) {
      toast.error("Please provide at least an email or a phone number.");
      return;
    }
    setBusy(true);
    try {
      const { data } = await api.post("/breeders", form);
      toast.success(`Breeder "${data.kennel_name || data.name}" added.`);
      onCreated?.(data);
      setForm({ name: "", kennel_name: "", location: "", email: "", phone: "", contact: "", bio: "" });
      onClose?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="sm:max-w-md" data-testid="add-breeder-dialog">
        <DialogHeader>
          <DialogTitle>Add a breeder</DialogTitle>
        </DialogHeader>
        <p className="text-[12px] text-[#6B6B6B] leading-relaxed -mt-1 mb-1">
          Use this when the breeder of your dog isn't already in the registry. If
          they later join and claim their kennel, an admin can merge the records.
          <br />
          <strong>Note:</strong> creating a breeder record here does NOT make them a
          Verified Kennel — that requires admin review.
        </p>
        <div className="space-y-3">
          {suggestions.length > 0 && (
            <div
              className="rounded-xl border border-amber-200 bg-amber-50/70 p-3"
              data-testid="add-breeder-suggestions"
            >
              <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-amber-900 mb-2">
                Did you mean…
              </p>
              <div className="flex flex-wrap gap-1.5">
                {suggestions.map(({ breeder: b }) => (
                  <button
                    key={b.id}
                    type="button"
                    onClick={() => pickExisting(b)}
                    className="inline-flex items-center gap-1 h-7 px-2.5 rounded-full bg-white border border-amber-300 text-stone-800 text-[11px] font-semibold hover:bg-amber-100 transition-colors"
                    data-testid={`add-breeder-suggest-${b.id}`}
                  >
                    {b.kennel_name || b.name}
                    {b.verified && (
                      <span className="inline-flex items-center justify-center h-3 w-3 rounded-full bg-gold text-[8px] font-bold text-ink ml-0.5" title="Verified">
                        ✓
                      </span>
                    )}
                  </button>
                ))}
              </div>
              <p className="text-[10.5px] text-amber-900/80 mt-2 leading-snug">
                Tap a match to select that existing breeder instead of creating a duplicate.
              </p>
            </div>
          )}
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
              Breeder name <span className="text-crimson">*</span>
            </Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g. Jane Doe"
              className="mt-1 rounded-lg"
              data-testid="new-breeder-name"
              autoFocus
            />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
              Kennel name (if any)
            </Label>
            <Input
              value={form.kennel_name}
              onChange={(e) => setForm({ ...form, kennel_name: e.target.value })}
              placeholder="e.g. Maple Ridge Cavaliers"
              className="mt-1 rounded-lg"
              data-testid="new-breeder-kennel"
            />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
              Location
            </Label>
            <Input
              value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              placeholder="e.g. Portland, OR"
              className="mt-1 rounded-lg"
              data-testid="new-breeder-location"
            />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
              Email <span className="text-crimson">*</span>
            </Label>
            <Input
              type="email"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
              placeholder="e.g. kennel@example.com"
              className="mt-1 rounded-lg"
              data-testid="new-breeder-email"
            />
            <p className="text-[10.5px] text-[#6B6B6B] mt-1 leading-snug">
              If the kennel owner has an Emergent account with this email,
              we'll auto-link the kennel so their Stripe Connect status
              flows into verification.
            </p>
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
              Phone <span className="text-[10px] text-[#6B6B6B] font-normal normal-case tracking-normal">(required if no email)</span>
            </Label>
            <Input
              type="tel"
              value={form.phone}
              onChange={(e) => setForm({ ...form, phone: e.target.value })}
              placeholder="e.g. 555-867-5309"
              className="mt-1 rounded-lg"
              data-testid="new-breeder-phone"
            />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
              Website / other
            </Label>
            <Input
              value={form.contact}
              onChange={(e) => setForm({ ...form, contact: e.target.value })}
              placeholder="optional — website, Instagram, etc."
              className="mt-1 rounded-lg"
              data-testid="new-breeder-contact"
            />
          </div>
          <div>
            <Label className="text-xs font-bold uppercase tracking-[0.2em] text-[#6B6B6B]">
              Notes
            </Label>
            <Textarea
              value={form.bio}
              onChange={(e) => setForm({ ...form, bio: e.target.value })}
              placeholder="optional — e.g. specialty, history, breed focus"
              className="mt-1 rounded-lg min-h-[72px]"
              data-testid="new-breeder-bio"
            />
          </div>
        </div>
        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onClose?.()}
            className="rounded-full"
            data-testid="add-breeder-cancel"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={busy}
            className="bg-[#0E0E0E] hover:bg-[#C1272D] rounded-full text-white"
            data-testid="add-breeder-save"
          >
            {busy ? "Adding…" : "Add breeder"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
